import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import { Divider } from "@mui/material";

import HeaderInfo from "../components/utils/HeaderInfo";
import DoubtBody from "../components/doubt/DoubtBody";
import LikesIsDone from "../components/utils/LikesIsDone";
import CommentMain from "../components/comment/CommentMain";
import NewAnswer from "../components/doubt/NewAnswer";
import Answer from "../components/doubt/Answer";
import Loading from "../components/utils/Loading";
import NotFound from "../components/utils/NotFound";
import useAuth from "../hooks/useAuth";
import { DISPLAYING_FOR } from "../config/AppEnums";

import "./css/doubt.css";

import { readDoubt } from "../functions/server-requests/doubt";
import { readManyAnswers } from "../functions/server-requests/answer";
import dateSlicer from "../functions/dateSlicer";

const Doubt = () => {
    const { doubtId } = useParams();
    const { auth } = useAuth();
    const [doubt, setDoubt] = useState(null);
    const [doubtDetails, setDoubtDetails] = useState({
        title: "",
        desc: ""
    });
    const [updateableUpdatedOn, setUpdateableUpdatedOn] = useState("");
    const [answers, setAnswers] = useState(null);

    useEffect(() => {
        const getDoubt = async (doubtId) => {
            const doubtRes = await readDoubt(doubtId);

            if (doubtRes.status === 200) {
                setDoubt(doubtRes.data.doubtDetails);

                setDoubtDetails({
                    ...doubtDetails,
                    title: doubtRes.data.doubtDetails.title,
                    desc: doubtRes.data.doubtDetails.desc
                });
                setUpdateableUpdatedOn(doubtRes.data.doubtDetails.updatedOn);

                document.title = `${doubtRes.data.doubtDetails.title} - Doubt - DSolve`;

                const answersRes = await readManyAnswers(doubtId);

                if (answersRes.status === 200) {
                    setAnswers(answersRes.data.answersDetails);
                }
                else {
                    setAnswers(undefined);
                }
            }
            else {
                setDoubt(undefined);
            }
        }

        getDoubt(doubtId);
    }, [doubtId]);

    if (doubt === null) {
        return <Loading />;
    }

    else if (doubt === undefined) {
        return <NotFound whatNotFound="Doubt" />;
    }

    else {
        const isUserAuthenticated = doubt.askedById === auth.userId;

        return (
            <div className="doubt">
                <div
                    className="doubt-part"
                    style={{ marginBottom: "18px" }} // var(--gap) + 2px
                >
                    <HeaderInfo
                        displayingFor={DISPLAYING_FOR.DOUBT}
                        userId={doubt.askedById}
                        name={doubt.askedBy}
                        askedOn={dateSlicer(doubt.askedOn)}
                        updatedOn={dateSlicer(updateableUpdatedOn)}
                        isUserAuthenticated={isUserAuthenticated}
                        data={{
                            contentOf: "doubt",
                            doubtId,
                            content: {
                                title: doubt.title,
                                body: doubt.desc
                            },
                            updateableData: {
                                doubtDetails,
                                setDoubtDetails,
                                setUpdateableUpdatedOn
                            }
                        }}
                    />

                    <DoubtBody
                        title={doubtDetails.title}
                        desc={doubtDetails.desc}
                    />

                    <LikesIsDone
                        displayingFor={DISPLAYING_FOR.DOUBT}
                        displayingForId={doubtId}
                        likedBy={doubt.likedBy}
                        likes={doubt.likes}
                        isDone={doubt.isSolved}
                        isUserAuthenticated={isUserAuthenticated}
                    />

                    <div className="doubt-comment-cnt">
                        <CommentMain
                            comments={doubt.comments}
                            commentForId={doubt._id}
                            commentFor="doubt"
                        />
                    </div>
                </div>

                <Divider
                    style={{ backgroundColor: "#1717171a" }} // var(--box-shadow)
                />

                <div className="doubt-part">
                    <div className="doubt-ans-cnt">
                        {answers === null
                            ? <Loading />
                            : answers === undefined
                                ? <NotFound whatNotFound="Answers" />
                                : <>
                                    <span className="title m-title">
                                        Answers ({answers.length})
                                    </span>

                                    {auth.isLoggedIn &&
                                        <NewAnswer
                                            doubtId={doubt._id}
                                            answeredById={auth.userId}
                                            answeredBy={auth.name}
                                            answers={answers}
                                            setAnswers={setAnswers}
                                        />
                                    }

                                    {answers.map((item, key) => {
                                        return (
                                            <Answer
                                                key={key}
                                                answer={item}
                                                isUserAuthenticated={isUserAuthenticated}
                                                updateableAnswers={answers}
                                                setUpdateableAnswers={setAnswers}
                                            />
                                        );
                                    })}
                                </>
                        }
                    </div>
                </div>
            </div>
        );
    }
}

export default Doubt;